import React, { useState, ReactNode, useEffect, useRef } from 'react';
import { Button } from '~/UIKit';
import { TrainerSelectionModal } from '../TrainerSelectionModal/TrainerSelectionModal';
import {
  requestProviderTrainerList,
  selectCurrentProvider,
  selectTrainerList,
} from '~/store/provider';
import { useDispatch, useSelector } from 'react-redux';
import { CHARCOAL_GREY, GREY } from '~/Constants/Colors.constants';
import { ITrainer } from '~/Models/DTO/Trainer.models';
import { ChevronRight } from '@mui/icons-material';
import { makeStyles } from '~/UIKit/Style';

interface TrainerSelectionInputProps {
  selectedTrainerIds: string[];
  children: ReactNode;
  updateTrainerList: (ids: string[]) => void;
  disabled: boolean;
}

const useTrainerSelectionModal = () => {
  // Hooks
  const dispatch = useDispatch();

  // State
  const [open, setOpen] = useState(false);

  // Action
  const loadTrainers = (id: string) => dispatch(requestProviderTrainerList(id));

  // Selectors
  const currentProvider = useSelector(selectCurrentProvider);
  const trainers = useSelector(selectTrainerList);

  useEffect(() => {
    if (trainers.length === 0 && currentProvider?.uuid) {
      loadTrainers(currentProvider.uuid);
    }
  }, [currentProvider]);

  return {
    open,
    setOpen,
    trainers,
  };
};

const useStyles = (trainerSelected: boolean) =>
  makeStyles(() => ({
    trainerSelectionInputWrapper: {
      width: '100%',
      display: 'flex',
      overflowWrap: 'anywhere',
    },
    button: {
      color: trainerSelected ? CHARCOAL_GREY : GREY,
      fontWeight: 'normal',
      marginLeft: 'auto',
      paddingRight: 0,
      // Offsets Chevron padding
      marginRight: '-8px',
    },
    ChevronRight: {
      color: GREY,
      display: 'inherit',
      alignItems: 'inherit',
      justifyContent: 'inherit',
    },
  }));

const getSelectedTrainerIds = (selectedTrainerIds: string[], trainers: ITrainer[]) =>
  selectedTrainerIds
    .map((id) =>
      trainers?.filter((trainer) => trainer.trainerId === id)?.map((trainer) => trainer.firstName),
    )
    ?.join(', ');

export const TrainerSelectionInput = ({
  disabled,
  children,
  selectedTrainerIds,
  updateTrainerList,
}: TrainerSelectionInputProps) => {
  const { classes } = useStyles(!!selectedTrainerIds?.length)();
  const { open, setOpen, trainers } = useTrainerSelectionModal();

  const buttonRef = useRef<HTMLButtonElement>(null);

  return (
    <div
      className={classes.trainerSelectionInputWrapper}
      data-testid="trainerselectioninput-wrapper"
    >
      <Button
        type="button"
        disabled={disabled}
        className={classes.button}
        onClick={() => setOpen(true)}
        data-testid="trainerselectioninput-button"
        ref={buttonRef}
      >
        {!selectedTrainerIds?.length
          ? children
          : getSelectedTrainerIds(selectedTrainerIds, trainers)}
        <span className={classes.ChevronRight}>
          <ChevronRight />
        </span>
      </Button>
      <TrainerSelectionModal
        open={open}
        onClose={() => setOpen(false)}
        updateTrainerList={updateTrainerList}
        selectedTrainerIds={selectedTrainerIds}
        data-testid="trainerselectioninput-modal"
        anchorEl={buttonRef.current}
      />
    </div>
  );
};
